import { type DataBlockArrayCreator } from './SHURDataBlockCreator'
import { type Household, HRKeys, type HRLine } from 'shur-api'
import { fallbackNoDetailsAvailableDataSection } from './SHUREmptyDataCreator'
import { coverageConceptIconData } from './SHURDataboxData'
import type { DataBlock } from 'de-zurich-ui-library'

import { t } from '@/i18n'

export class SHURDataHouseholdcontentsCreator implements DataBlockArrayCreator {
  createDatablockArray(hrLine: HRLine, household: Household): DataBlock[] {
    return [
      createHouseholdGlobalData(hrLine, household),
      createHouseholdPlace(household),
      createInsuredhouseholdContent(household)
    ]
  }
}

// Versicherungsumfang
function createHouseholdGlobalData(hrLine: HRLine, household: Household): DataBlock {
  if (!household.details || household.details.length === 0)
    return fallbackNoDetailsAvailableDataSection()

  const contractDetailsCodeValuePairs = household.details

  return <DataBlock>{
    title: {
      label: t('shurDetails.sections.HOUSEHOLD.SCOPE_OF_INSURANCE')
    },
    data: [
      {
        label: t('shurDetails.sections.coverageConcept.title'),
        value: contractDetailsCodeValuePairs.some(
          (entry) => entry.key === HRKeys.CoverageConcept
        )
          ? t(
              `shurDetails.sections.coverageConcept.${
                contractDetailsCodeValuePairs.find(
                  (entry) => entry.key === HRKeys.CoverageConcept
                )!.value
              }`
            )
          : null,
        iconSpan: contractDetailsCodeValuePairs.some(
          (entry) => entry.key === HRKeys.CoverageConcept
        )
          ? coverageConceptIconData(
              String(
                contractDetailsCodeValuePairs.find(
                  (entry) => entry.key === HRKeys.CoverageConcept
                )?.value
              ),
              hrLine.productSubType!,
              String(
                contractDetailsCodeValuePairs.find(
                  (entry) => entry.key === HRKeys.ProductVersion
                )?.value
              )
            )
          : null
      },
      {
        label: t(`shurDetails.sections.HOUSEHOLD.${HRKeys.HouseholdGlass}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HRKeys.HouseholdGlass
        )?.value
      },
      {
        label: t(`shurDetails.sections.HOUSEHOLD.${HRKeys.HouseholdWithoutGlass}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HRKeys.HouseholdWithoutGlass
        )?.value
      },
      {
        label: t(`shurDetails.sections.HOUSEHOLD.${HRKeys.GlassBuiltin}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HRKeys.GlassBuiltin
        )?.value
      },
      {
        label: t(`shurDetails.sections.HOUSEHOLD.${HRKeys.HouseholdElem}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HRKeys.HouseholdElem
        )?.value
      },
      {
        label: t(`shurDetails.sections.HOUSEHOLD.${HRKeys.Glass}`),
        value: contractDetailsCodeValuePairs.find((entry) => entry.key === HRKeys.Glass)
          ?.value
      },
      {
        label: t(`shurDetails.sections.HOUSEHOLD.${HRKeys.DeductibleHuoseholde}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HRKeys.DeductibleHuoseholde
        )?.value
      },
      {
        label: t(`shurDetails.sections.HOUSEHOLD.${HRKeys.DeductibleGlass}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HRKeys.DeductibleGlass
        )?.value
      },
      {
        label: t(`shurDetails.sections.HOUSEHOLD.${HRKeys.HouseholdElemFurther}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HRKeys.HouseholdElemFurther
        )?.value
      },
      {
        label: t(`shurDetails.sections.${HRKeys.InstantCoverage}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HRKeys.InstantCoverage
        )?.value
      }
    ]
  }
}

// Versicherungsort
function createHouseholdPlace(household: Household): DataBlock {
  if (!household.details || household.details.length === 0)
    return fallbackNoDetailsAvailableDataSection()

  const contractDetailsCodeValuePairs = household.details

  return <DataBlock>{
    title: {
      label:
        t('shurDetails.sections.insuredPlace.title') ??
        'shurDetails.sections.insuredPlace.title'
    },
    data: [
      {
        label: t(`shurDetails.sections.insuredPlace.${HRKeys.StreetAndNumber}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HRKeys.StreetAndNumber
        )?.value
      },
      {
        label: t(`shurDetails.sections.insuredPlace.${HRKeys.ZipcodeAndCity}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HRKeys.ZipcodeAndCity
        )?.value
      },
      {
        label: t(`shurDetails.sections.insuredPlace.${HRKeys.AppartmentArea}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HRKeys.AppartmentArea
        )?.value
      }
    ]
  }
}

// Versicherte Sache
function createInsuredhouseholdContent(household: Household): DataBlock {
  if (!household.details || household.details.length === 0)
    return fallbackNoDetailsAvailableDataSection()

  const contractDetailsCodeValuePairs = household.details

  return <DataBlock>{
    title: {
      label: t('shurDetails.sections.insuredThings.title')
    },
    data: [
      {
        label: t(`shurDetails.sections.insuredThings.HOUSEHOLD_EXISTS`),
        value: t(`shurDetails.sections.insuredThings.HOUSEHOLD_EXISTS_VALUE`)
      },
      {
        label: t(`shurDetails.sections.insuredThings.${HRKeys.HouseholdCashOrCard}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HRKeys.HouseholdCashOrCard
        )?.value
      },
      {
        label: t(
          `shurDetails.sections.insuredThings.${HRKeys.HouseholdValuableCoverage}`
        ),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HRKeys.HouseholdValuableCoverage
        )?.value
      },
      {
        label: t(`shurDetails.sections.insuredThings.${HRKeys.HouseholdBike}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HRKeys.HouseholdBike
        )?.value
      },
      {
        label: t(`shurDetails.sections.insuredThings.${HRKeys.HouseholdSport}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HRKeys.HouseholdSport
        )?.value
      },
      {
        label: t(`shurDetails.sections.insuredThings.${HRKeys.Homeassistance}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HRKeys.Homeassistance
        )?.value
      },
      {
        label: t(`shurDetails.sections.insuredThings.${HRKeys.HomeassistanceTotalCov}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HRKeys.HomeassistanceTotalCov
        )?.value
      }
    ]
  }
}
